import { pageNumberingFrontMatterOptions } from '@/constants/pageNumbering'
import type { InjectionKey, Ref } from 'vue'
import type { TPrintBookPageNumbering } from '@/types/print-books'

export const defaultPageNumbering: TPrintBookPageNumbering = { startPage: 1, frontMatter: 'lower-roman' }

export const pageNumberingKey: InjectionKey<Ref<TPrintBookPageNumbering>> = Symbol('pageNumbering')

const romanNumerals: [number, string][] = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
]

const maxStartPage = 999

const paginationSelector = ".tiptap[class*='tiptap-pages'] [data-tiptap-pagination]"

export function toRoman(value: number): string {
  let rest = Math.floor(value)
  let result = ''

  for (const [arabic, roman] of romanNumerals) {
    while (rest >= arabic) {
      result += roman
      rest -= arabic
    }
  }

  return result
}

export function normalizePageNumbering(value?: Partial<TPrintBookPageNumbering> | null): TPrintBookPageNumbering {
  const startPage = Math.floor(Number(value?.startPage))
  const frontMatter = value?.frontMatter

  return {
    startPage:
      Number.isFinite(startPage) && startPage > 0 ? Math.min(startPage, maxStartPage) : defaultPageNumbering.startPage,
    frontMatter:
      pageNumberingFrontMatterOptions.find((option) => option.name === frontMatter)?.name ??
      defaultPageNumbering.frontMatter,
  }
}

export function getPageLabel(page: number, numbering: TPrintBookPageNumbering): string {
  if (page >= numbering.startPage) return String(page - numbering.startPage + 1)
  if (numbering.frontMatter === 'none') return ''

  const roman = toRoman(page)

  return numbering.frontMatter === 'lower-roman' ? roman.toLowerCase() : roman
}

export function buildPageNumberingCss(numbering: TPrintBookPageNumbering): string {
  const footer = `${paginationSelector} .tiptap-page-footer`
  const rules = [
    `${paginationSelector} { counter-reset: des-page ${1 - numbering.startPage}; }`,
    `${footer} { counter-increment: page-number des-page; }`,
    `${footer} p::before { content: counter(des-page); }`,
  ]

  for (let page = 1; page < numbering.startPage; page++) {
    const label = getPageLabel(page, numbering)
    const content = label ? `"${label}"` : 'none'

    rules.push(`${footer}[data-footer-page-number="${page}"] p::before { content: ${content}; }`)
  }

  return rules.join('\n')
}
